import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  message,
  Modal,
  Input,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
} from "antd";
import { PlusOutlined, EditOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import axios from "axios";
import AgreementForm from "./agreementForm";

const { Search } = Input;
const { Title } = Typography;

const AgreementList = () => {
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [formData, setFormData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const response = await axios.get("http://93.127.203.113:5005/agreements");
      setAgreements(response.data);
      setFilteredAgreements(response.data);
    } catch (error) {
      message.error("Failed to fetch agreements.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerSearch = value.toLowerCase();
    const filtered = agreements.filter((record) => {
      const fieldsToSearch = [
        record.owner?.name || "",
        record.vehicle?.plateNumber || "",
        record.pricePerDay?.toString() || "",
        new Date(record.startDate).toLocaleDateString(),
        new Date(record.endDate).toLocaleDateString() || "N/A",
        record.inspection?.fuelStatus || "",
        record.inspection?.status || "",
      ];
      return fieldsToSearch.some((field) =>
        field.toLowerCase().includes(lowerSearch)
      );
    });
    setFilteredAgreements(filtered);
  };

  const highlightText = (text, searchText) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = text.toString().split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#fffb8f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleEdit = (record) => {
    setFormData(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: <span style={{ fontWeight: "bold" }}>Owner Name</span>,
      dataIndex: ["owner", "name"],
      key: "ownerName",
      render: (text) => highlightText(text || "N/A", searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Vehicle</span>,
      dataIndex: ["vehicle", "plateNumber"],
      key: "vehicle",
      render: (text) => highlightText(text || "N/A", searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Price Per Day</span>,
      dataIndex: "pricePerDay",
      key: "pricePerDay",
      render: (text) => highlightText(`$${text}`, searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Start Date</span>,
      dataIndex: "startDate",
      key: "startDate",
      render: (date) =>
        highlightText(new Date(date).toLocaleDateString() || "N/A", searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>End Date</span>,
      dataIndex: "endDate",
      key: "endDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString() : "N/A",
          searchText
        ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Inspection Date</span>,
      dataIndex: ["inspection", "inspectionDate"],
      key: "inspectionDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString() : "N/A",
          searchText
        ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Fuel Status</span>,
      dataIndex: ["inspection", "fuelStatus"],
      key: "fuelStatus",
      render: (text) => highlightText(text || "N/A", searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Inspection Status</span>,
      dataIndex: "inspection",
      key: "inspectionStatus",
      render: (inspection) => {
        const displayStatus = inspection?.status ?? "Active"; // Default to "Active" if null/undefined
        return (
          <Tag
            color={displayStatus === "Active" ? "green" : "red"}
            style={{ fontWeight: "bold" }}
          >
            {highlightText(displayStatus, searchText)}
          </Tag>
        );
      },
    },
    
    {
      title: <span style={{ fontWeight: "bold" }}>Actions</span>,
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: "#0c8c49", marginRight: "8px" }}
          >
            Edit
          </Button>
          <Button type="link">
            <NavLink
              to={`/agreementDetails/${record.agreementId}`}
              style={{ color: "#0c8c49" }}
            >
              <InfoCircleOutlined /> &nbsp;Details
            </NavLink>
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderTop: "5px solid #0c8c49",
        }}
      >
        <Title level={4} style={{ color: "#0c8c49", marginBottom: "20px" }}>
          Agreements
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col span={18}>
            <Search
              placeholder="Search Agreements"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: "60%" }}
            />
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setFormData({});
                setModalVisible(true);
              }}
              style={{
                backgroundColor: "#0c8c49",
                borderColor: "#0c8c49",
              }}
            >
              Add Agreement
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredAgreements}
            rowKey="agreementId"
            pagination={{ pageSize: 5 }}
            bordered
          />
        )}
      </Card>

      <Modal
        title={formData.agreementId ? "Edit Agreement" : "Add Agreement"}
        visible={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        centered
        width="50%"
        style={{ borderRadius: "10px" }}
        bodyStyle={{
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <AgreementForm
          formData={formData}
          setFormData={setFormData}
          closeModal={() => setModalVisible(false)}
          refetchData={fetchAgreements}
        />
      </Modal>
    </div>
  );
};

export default AgreementList;
